<template>
  <div
    v-if="model"
    class="modal fade show d-block"
    tabindex="-1"
    @click.self="handleClose"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Grexel User</h5>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleAddUser">
            <!-- First Name -->
            <div class="mb-3">
              <label for="firstName" class="form-label"
                >First name <span class="text-danger">*</span></label
              >
              <input
                type="text"
                class="form-control"
                id="firstName"
                v-model="addUserForm.firstName"
                placeholder="First name"
                required
              />
            </div>

            <!-- Last Name -->
            <div class="mb-3">
              <label for="lastName" class="form-label"
                >Last name <span class="text-danger">*</span></label
              >
              <input
                type="text"
                class="form-control"
                id="lastName"
                v-model="addUserForm.lastName"
                placeholder="Last name"
                required
              />
            </div>

            <!-- Email -->
            <div class="mb-3">
              <label for="email" class="form-label"
                >Email <span class="text-danger">*</span></label
              >
              <input
                type="email"
                class="form-control"
                id="email"
                v-model="addUserForm.email"
                placeholder="Email"
                required
              />
            </div>

            <!-- Apply To -->
            <div class="mb-3">
              <label class="form-label"
                >Apply to <span class="text-danger">*</span></label
              >
              <div class="d-flex gap-3">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    id="applyToAll"
                    value="all"
                    v-model="addUserForm.applyToMode"
                    @change="handleApplyToModeChange"
                  />
                  <label class="form-check-label" for="applyToAll">
                    All companies
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    id="applyToSelected"
                    value="selected"
                    v-model="addUserForm.applyToMode"
                    @change="handleApplyToModeChange"
                  />
                  <label class="form-check-label" for="applyToSelected">
                    Selected companies
                  </label>
                </div>
              </div>
            </div>

            <!-- Company Selection -->
            <div v-if="addUserForm.applyToMode === 'selected'" class="mb-3">
              <label class="form-label">Companies</label>
              <CompanySearchDropdown
                ref="companySearchDropdownRef"
                :autoFetch="false"
                :withLabel="false"
                :withDescription="false"
                :placeholder="'Search and select companies...'"
                @select="handleCompanySelect"
              />
              <small class="form-text text-muted">
                Note: Only companies with Grexel connections will be affected.
                Use Preview Mode to see which organizations will be updated.
              </small>
              <div v-if="selectedCompanies.length > 0" class="mt-2">
                <div class="d-flex flex-wrap gap-2">
                  <span
                    v-for="company in selectedCompanies"
                    :key="company.id"
                    class="badge bg-primary"
                  >
                    {{ company.name }}
                    <button
                      type="button"
                      class="btn-close btn-close-white ms-2"
                      style="font-size: 0.6rem"
                      @click="removeCompany(company.id)"
                    ></button>
                  </span>
                </div>
              </div>
            </div>
          </form>

          <!-- Dry Run Preview Results -->
          <div v-if="dryRunPreview" class="alert alert-info mt-3">
            <h6 class="alert-heading">Dry Run Preview</h6>
            <p class="mb-2">
              <strong>Total organizations to be updated:</strong>
              {{ dryRunPreview.total_targets }}
            </p>
            <div v-if="dryRunPreview.target_ids.length > 0">
              <strong>User will be added to these Grexel organizations:</strong>
              <pre
                class="border rounded p-3 mb-0 mt-1 bg-white text-dark"
                style="
                  max-height: 300px;
                  overflow-y: auto;
                  font-size: 0.875rem;
                  line-height: 1.5;
                "
                >{{ dryRunPreview.target_ids.join("\n") }}</pre>
            </div>
            <p v-else class="mb-0 text-warning">
              No target organizations found for the selected criteria.
            </p>
          </div>
        </div>
        <div class="modal-footer d-flex justify-content-between">
          <div class="form-check form-switch">
            <input
              type="checkbox"
              class="form-check-input"
              id="dryRunToggle"
              v-model="dryRunEnabled"
              @change="dryRunPreview = null"
            />
            <label class="form-check-label" for="dryRunToggle">
              Preview Mode
            </label>
          </div>
          <div class="d-flex gap-2">
            <button
              type="button"
              class="btn btn-secondary"
              @click="handleClose"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="handleAddUser"
              :disabled="isSubmittingAddUser"
            >
              <span
                v-if="isSubmittingAddUser"
                class="spinner-border spinner-border-sm me-2"
                role="status"
              ></span>
              {{ submitButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="model" class="modal-backdrop fade show"></div>
</template>

<script setup lang="ts">
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { computed, ref, watch } from "vue";

// TypeScript interfaces
interface AddGrexelUserRequest {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    mobile_phone?: string;
    role: string;
  };
  all_companies: boolean;
  company_ids: number[];
  registry_company_ids?: string[];
  dry_run: boolean;
}

interface AddGrexelUserResponse {
  success: boolean;
  message: string;
  total_targets?: number;
  target_ids?: string[];
}

interface DryRunPreview {
  total_targets: number;
  target_ids: string[];
}

interface AdminCompany {
  id: number;
  name: string;
  country?: string;
}

const emit = defineEmits<{
  close: [];
}>();

const model = defineModel<boolean>({ required: true });

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

// Modal state
const isSubmittingAddUser = ref(false);
const dryRunEnabled = ref(false);
const dryRunPreview = ref<DryRunPreview | null>(null);
const addUserForm = ref({
  firstName: "",
  lastName: "",
  email: "",
  applyToMode: "all" as "all" | "selected",
  selectedCompanyIds: [] as number[],
});
const selectedCompanies = ref<AdminCompany[]>([]);
const companySearchDropdownRef = ref<InstanceType<
  typeof CompanySearchDropdown
> | null>(null);

// Computed property for dynamic button text
const submitButtonText = computed(() => {
  return dryRunEnabled.value ? "Run Preview" : "Add User";
});

// Reset form when modal opens
watch(model, (isOpen) => {
  if (isOpen) {
    addUserForm.value = {
      firstName: "",
      lastName: "",
      email: "",
      applyToMode: "all",
      selectedCompanyIds: [],
    };
    selectedCompanies.value = [];
    dryRunEnabled.value = false;
    dryRunPreview.value = null;
  }
});

// Clear preview when form criteria change
watch(
  () => [
    addUserForm.value.applyToMode,
    addUserForm.value.selectedCompanyIds.length,
  ],
  () => {
    dryRunPreview.value = null;
  }
);

const handleClose = () => {
  model.value = false;
  emit("close");
};

const handleCompanySelect = (company: AdminCompany) => {
  if (!addUserForm.value.selectedCompanyIds.includes(company.id)) {
    addUserForm.value.selectedCompanyIds.push(company.id);
    selectedCompanies.value.push(company);
  }
};

const removeCompany = (companyId: number) => {
  addUserForm.value.selectedCompanyIds =
    addUserForm.value.selectedCompanyIds.filter((id) => id !== companyId);
  selectedCompanies.value = selectedCompanies.value.filter(
    (c) => c.id !== companyId
  );
};

const handleApplyToModeChange = async () => {
  if (addUserForm.value.applyToMode === "selected") {
    // Switching to "Selected companies" - fetch companies for dropdown
    if (companySearchDropdownRef.value) {
      await companySearchDropdownRef.value.fetchCompanies();
    }
  } else {
    // Switching to "All companies" - clear selected companies
    addUserForm.value.selectedCompanyIds = [];
    selectedCompanies.value = [];
  }
};

const handleAddUser = async () => {
  if (
    !addUserForm.value.firstName.trim() ||
    !addUserForm.value.lastName.trim() ||
    !addUserForm.value.email.trim()
  ) {
    globalErrorHandler("Please fill in all required fields");
    return;
  }

  if (
    addUserForm.value.applyToMode === "selected" &&
    addUserForm.value.selectedCompanyIds.length === 0
  ) {
    globalErrorHandler("Please select at least one company");
    return;
  }

  try {
    isSubmittingAddUser.value = true;
    const payload: AddGrexelUserRequest = {
      user: {
        first_name: addUserForm.value.firstName.trim(),
        last_name: addUserForm.value.lastName.trim(),
        email: addUserForm.value.email.trim(),
        role: "AH_ROOT",
      },
      all_companies: addUserForm.value.applyToMode === "all",
      company_ids:
        addUserForm.value.applyToMode === "selected"
          ? addUserForm.value.selectedCompanyIds
          : [],
      dry_run: dryRunEnabled.value,
    };

    const response = await goSolidApi.post<AddGrexelUserResponse>(
      "/api/admin/registry/users",
      payload
    );

    if (response.data.success) {
      if (dryRunEnabled.value) {
        // Preview mode: Store results and keep modal open
        if (
          response.data.total_targets !== undefined &&
          response.data.target_ids !== undefined
        ) {
          dryRunPreview.value = {
            total_targets: response.data.total_targets,
            target_ids: response.data.target_ids,
          };
        }
        globalSuccessHandler("Preview Complete", response.data.message);
      } else {
        // Execute mode: Close modal
        globalSuccessHandler("Success", response.data.message);
        handleClose();
      }
    }
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isSubmittingAddUser.value = false;
  }
};
</script>

<style scoped lang="scss"></style>
